import React from "react";
import clsx from "clsx";
import { LuBot } from "react-icons/lu";
import styles from "./styles.module.scss";
import Model from "./components/model";
import Answers from "./components/answers";
import DetailData from "./components/detail-data";
import AnchorTextList from "./components/anchor-text-list";
import CopyButton from "./components/copy-button";
import ShareButton from "./components/share-button";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { SplitText } from "gsap/SplitText";
import type { Message } from "../../../contexts/chat-log/chat-log-context";
import { useChatbotConfig } from "../../../contexts/chatbot-config";
import CtaCarousel from "../cta-carousel";
import { useCtaClick } from "../cta-carousel/use-cta-click";
import { buildCtaEntries } from "../../../libs/cta";

gsap.registerPlugin(SplitText);

/** ローディング中に循環表示する待機メッセージ */
const WAITING_TEXTS = [
  '最適な回答を考えています。',
  '今日は、お越しくださりありがとうございます。',
  '遠慮なくご相談くださいね。',
  '最適なご案内まで少しお待ちください。',
]

function MessageAi({
  className,
  message,
  onAnswerClick,
  disabled,
  ...props
}: Omit<React.ComponentPropsWithoutRef<"div">, "children"> & {
  message: Message;
  onAnswerClick?: (answer: string) => void;
  disabled?: boolean;
}) {
  const container = React.useRef<HTMLDivElement>(null);
  const modelRef = React.useRef<HTMLDivElement>(null);
  const { chatbotData, chatDisplayCtaData } = useChatbotConfig();
  const handleCtaClick = useCtaClick();
  const chatCtas = React.useMemo(
    () => buildCtaEntries(chatDisplayCtaData, chatbotData?.chatbot_S3_URL || ""),
    [chatDisplayCtaData, chatbotData?.chatbot_S3_URL],
  );
  const ctaLinkItems = (message.cta_data ?? [])
    .map((cta: Record<string, string | undefined>, index: number) => ({
      key: index,
      href: cta.url || cta.link || cta.cta_link || '',
      label: cta.anchor_text || cta.title || cta.description || '詳細はこちら',
    }))
    .filter((item: { href: string }) => item.href && item.href !== '#');
  const avatarUrl = chatbotData?.chatbot_icon?.trim();
  const [showAvatar, setShowAvatar] = React.useState<boolean>(!!avatarUrl);

  React.useEffect(() => {
    setShowAvatar(!!avatarUrl);
  }, [avatarUrl]);

  const { displayModelText, displayAnswers, displayDetailData } = React.useMemo(() => {
    const answers = message.answer || [];
    const detailData = message.asset_data || [];

    // ストリーミング完了後はfns.tsが既にJSONを除去済みなのでそのまま返す
    if (message.streaming_completed !== false) {
      return { displayModelText: message.model, displayAnswers: answers, displayDetailData: detailData };
    }

    // ストリーミング中: '{' 以降に現れる不完全なJSONを隠す
    const openBraceIndex = message.model.indexOf('{');
    const modelText = openBraceIndex !== -1
      ? message.model.substring(0, openBraceIndex).trim()
      : message.model;

    return { displayModelText: modelText, displayAnswers: answers, displayDetailData: detailData };
  }, [message.model, message.answer, message.asset_data, message.streaming_completed]);

  // フロントエンドの sortDetailDataByDisplayOrder に合わせて表示順序をソート
  // 動画(1) → 画像(2) → 資料(3) → その他(4)
  const filteredDetailData = React.useMemo(() => {
    const getDisplayOrder = (fileType: string): number => {
      const type = (fileType || '').toUpperCase();
      if (type === 'YOUTUBE' || type === 'YOUTUBE動画' || type === 'SHORTS' ||
        type === 'ショーツ' || type === 'ショーツ動画' || type === 'YOUTUBE SHORTS' ||
        type === 'VIDEO' || type === 'ショート動画' || type === 'MP4' ||
        type === 'MOV' || type === 'WEBM' || type === 'AVI') return 1;
      if (type === 'DATASET' || type === 'IMAGE_SET' || type === 'IMAGESET' ||
        type === 'IMG' || type === 'PNG' || type === 'JPG' ||
        type === 'JPEG' || type === 'GIF' || type === 'WEBP' || type === 'BMP') return 2;
      if (type === 'PDF' || type === 'PPTX' || type === 'XLSX' || type === 'DOCX' ||
        type === 'ナレッジRAGデータ' || type.includes('RAG') || type.includes('LINK')) return 3;
      return 4;
    };
    return [...displayDetailData].sort((a, b) => getDisplayOrder(a.file_type) - getDisplayOrder(b.file_type));
  }, [displayDetailData]);

  // 段階的表示ロジック: 本文（display_text）の表示完了のみを遅延対象とし（isLoadingで制御）、
  // それ以外のデータ（display_link/asset_description/ai_question/answers/campaign_text/cta_data）は
  // 自分のフィールドが解析され次第即座に表示する（下記の各JSX条件で直接判定）。

  const isLoading = message.streaming_completed === false && !displayModelText;

  const isAvatarAnimating = message.streaming_completed === false;

  // 待機テキスト循環タイマー
  const [waitingTextIndex, setWaitingTextIndex] = React.useState(0)
  const waitingTimerRef = React.useRef<ReturnType<typeof setInterval> | null>(null)
  const hasAnimated = React.useRef(false)

  React.useEffect(() => {
    if (isLoading) {
      setWaitingTextIndex(0)
      waitingTimerRef.current = setInterval(() => {
        setWaitingTextIndex((prev) => (prev + 1) % WAITING_TEXTS.length)
      }, 1500)
    } else {
      if (waitingTimerRef.current) {
        clearInterval(waitingTimerRef.current)
        waitingTimerRef.current = null
      }
    }
    return () => {
      if (waitingTimerRef.current) {
        clearInterval(waitingTimerRef.current)
        waitingTimerRef.current = null
      }
    }
  }, [isLoading])

  useGSAP(
    () => {
      // ストリーミング中はDOMが動的更新されるためアニメーションをスキップ
      if (message.streaming_completed === false) return;
      // 同じメッセージインスタンスで二重実行を防止
      if (hasAnimated.current) return;
      hasAnimated.current = true;

      const tl = gsap.timeline();
      const els = modelRef.current?.querySelectorAll(':scope > *');
      els?.forEach((el) => {
        const text = SplitText.create(el, { type: 'chars', tag: 'span' });
        tl.add(
          gsap.from(text.chars, {
            autoAlpha: 0,
            duration: 0.3,
            stagger: 0.01,
          }),
        );
      });
    },
    { scope: container, dependencies: [message.streaming_completed] },
  );

  return (
    <>
      <div
        className={clsx(styles._onetouchai_messageAi_wrapper, className)}
        {...props}
        ref={container}
      >
        <div className={styles._onetouchai_messageAi}>
          <header className={styles._onetouchai_header}>
            {showAvatar && (
              <img
                className={clsx(
                  styles._onetouchai_avatar,
                  isAvatarAnimating && styles._onetouchai_avatar_loading
                )}
                src={avatarUrl}
                alt=""
                width="42"
                height="42"
                onError={() => setShowAvatar(false)}
              />
            )}
            <div className={styles._onetouchai_tools}>
              <ShareButton ref={modelRef} />
              <CopyButton ref={modelRef} />
            </div>
          </header>
          <div className={styles._onetouchai_body}>
            {isLoading ? (
              <div className={styles._onetouchai_typing_indicator}>
                <span
                  key={waitingTextIndex}
                  className={styles._onetouchai_waiting_text}
                >
                  {WAITING_TEXTS[waitingTextIndex]}
                </span>
              </div>
            ) : (
              <>
                <Model model={displayModelText} ref={modelRef} />

                {/* display_link: display_textの直後にアンカーテキストとして表示（到着した時点で即座に表示） */}
                {message.display_link && message.display_link.length > 0 && (
                  <AnchorTextList
                    items={message.display_link
                      .map((link: any, index: number) => ({
                        key: index,
                        href: link?.url || '',
                        label: link?.anchor_text || '',
                      }))
                      .filter((item: { href: string; label: string }) => item.href && item.label)}
                  />
                )}

                {/* キャンペーンテキスト: 4行フォーマットのマークダウン文字列を表示（平文行のみ、到着した時点で即座に表示） */}
                {message.campaign_text && message.campaign_text.trim() && (() => {
                  const lines = message.campaign_text.split('\n');
                  const plainLines = lines.filter((line) => !line.match(/^\[(.+?)\]\((.+?)\)$/));
                  const linkItems = lines
                    .map((line, i) => {
                      const linkMatch = line.match(/^\[(.+?)\]\((.+?)\)$/);
                      return linkMatch ? { key: i, href: linkMatch[2], label: linkMatch[1] } : null;
                    })
                    .filter((item): item is { key: number; href: string; label: string } => item !== null);
                  const hasPlainText = plainLines.some((line) => line.trim());
                  if (!hasPlainText && linkItems.length === 0) return null;
                  return (
                    <div style={{ marginTop: '12px', backgroundColor: '#eef6fd', borderRadius: '8px', padding: '12px 16px' }}>
                      {hasPlainText && plainLines.map((line, i) => (
                        line.trim() ? (
                          <p key={i} style={{ fontSize: '13px', margin: '0 0 2px 0', color: '#333' }}>{line}</p>
                        ) : null
                      ))}
                      <AnchorTextList items={linkItems} marginTop="8px" />
                    </div>
                  );
                })()}

                {/* バナーCTA: セッションリセット時に表示されるリンク画像バナー */}
                {message.banner_cta_data && message.banner_cta_data.length > 0 && (() => {
                  const baseUrl = chatbotData?.chatbot_S3_URL || '';
                  const encodeS3Path = (path: string) => {
                    if (!path) return '';
                    return path.split('/').map(part => encodeURIComponent(part)).join('/');
                  };
                  return (
                    <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {message.banner_cta_data.map((cta: any, index: number) => {
                        const imgSrc = cta.image?.startsWith('http')
                          ? cta.image
                          : `${baseUrl}/${encodeS3Path(cta.image)}`;
                        const href = cta.link || cta.cta_link || cta.url;
                        if (!imgSrc || !cta.image) return null;
                        return href ? (
                          <a
                            key={index}
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles._onetouchai_bannerCta}
                            style={{ display: 'block', width: '100%', aspectRatio: '300 / 80', overflow: 'hidden', borderRadius: '8px' }}
                          >
                            <img
                              src={imgSrc}
                              alt={cta.title || ''}
                              style={{ width: '100%', height: '100%', objectFit: 'fill', display: 'block' }}
                            />
                          </a>
                        ) : (
                          <div key={index} className={styles._onetouchai_bannerCta} style={{ display: 'block', width: '100%', aspectRatio: '300 / 80', overflow: 'hidden', borderRadius: '8px' }}>
                            <img
                              src={imgSrc}
                              alt={cta.title || ''}
                              style={{ width: '100%', height: '100%', objectFit: 'fill', display: 'block' }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        </div>

        {/* asset_description + アセット（吹き出しの外側、/chat と同じく ai_question より前に表示） */}
        {!isLoading && filteredDetailData.length > 0 && (
          <div style={{ width: "100%", boxSizing: "border-box", overflow: "hidden" }}>
            {message.asset_description && (
              <p style={{
                fontSize: '12px',
                lineHeight: '1.6',
                color: '#000',
                fontWeight: 400,
                whiteSpace: 'pre-wrap',
                marginTop: '26px',
                marginBottom: 0,
                paddingLeft: '24px',
              }}>
                {message.asset_description}
              </p>
            )}
            <div style={{ marginTop: message.asset_description ? '10px' : '26px' }}>
              <DetailData detailData={filteredDetailData} />
            </div>
          </div>
        )}

        {/* AI提案テキスト（お聞かせください） - 吹き出しの外側に配置
            必ずisLoadingでゲートする（answersと同じタイミングで表示しないと、
            ボタンより先に単独表示されるバグが発生するため） */}
        {!isLoading && message.ai_question && message.ai_question.trim() && (
          <div style={{ marginTop: '20px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '9999px',
              border: '2px solid #ccc',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              backgroundColor: '#fff',
            }}>
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt=""
                  width="32"
                  height="32"
                  style={{ objectFit: 'cover' }}
                />
              ) : (
                <LuBot style={{ width: '18px', height: '18px', color: '#374151' }} />
              )}
            </div>
            <div style={{
              position: 'relative',
              flex: 1,
              minWidth: 0,
              backgroundColor: '#fff9dd',
              borderRadius: '16px',
              padding: '12px 16px',
            }}>
              <div style={{
                position: 'absolute',
                left: '-6px',
                top: '14px',
                width: 0,
                height: 0,
                borderTop: '6px solid transparent',
                borderBottom: '6px solid transparent',
                borderRight: '6px solid #fff9dd',
              }} />
              <p style={{ fontSize: '14px', fontWeight: 700, color: '#f5a623', margin: 0 }}>お聞かせください</p>
              <p style={{
                fontSize: '14px',
                lineHeight: '1.6',
                color: '#000',
                margin: '10px 0 0 0',
                whiteSpace: 'pre-wrap',
              }}>
                {message.ai_question}
              </p>
            </div>
          </div>
        )}

        {!isLoading && !message.answers_used && displayAnswers.length > 0 && onAnswerClick && (
          <div style={{ marginTop: '20px' }}>
            <p style={{ fontSize: '12px', color: '#000', fontWeight: 400, margin: '0 0 6px 0' }}>おすすめの選択肢</p>
            <Answers
              answers={displayAnswers}
              onClick={onAnswerClick}
              disabled={disabled}
            />
          </div>
        )}

        {/* チャット内CTA: need_cta時のみ。管理画面のCTAを優先し、無ければプロンプト生成CTAのアンカーテキスト */}
        {!isLoading && message.need_cta && (
          chatCtas.length > 0 ? (
            <div style={{ marginTop: '20px' }}>
              <div style={{ borderTop: '1px dashed #ccc', marginBottom: '8px' }} />
              <p style={{ fontSize: '12px', color: '#000', fontWeight: 400, margin: '0 0 6px 0' }}>ご相談はこちら</p>
              <CtaCarousel ctas={chatCtas} onCtaClick={handleCtaClick} />
            </div>
          ) : ctaLinkItems.length > 0 ? (
            <div style={{ marginTop: '20px' }}>
              <div style={{ borderTop: '1px dashed #ccc', marginBottom: '8px' }} />
              <p style={{ fontSize: '12px', color: '#000', fontWeight: 400, margin: '0 0 6px 0' }}>ご相談はこちら</p>
              <AnchorTextList items={ctaLinkItems} />
            </div>
          ) : null
        )}
      </div>
    </>
  );
}

// メモ化して完了したメッセージは再レンダリングしない
// streaming_completed が false の場合のみ再レンダリングを許可
export default React.memo(MessageAi, (prevProps, nextProps) => {
  // ストリーミング中のメッセージは常に再レンダリング
  if (nextProps.message.streaming_completed === false) {
    return false; // 再レンダリングする
  }

  // 完了したメッセージは内容が変わらない限り再レンダリングしない
  return (
    prevProps.message.model === nextProps.message.model &&
    prevProps.message.streaming_completed === nextProps.message.streaming_completed &&
    prevProps.message.answers_used === nextProps.message.answers_used &&
    prevProps.message.ai_question === nextProps.message.ai_question &&
    JSON.stringify(prevProps.message.answer) === JSON.stringify(nextProps.message.answer) &&
    JSON.stringify(prevProps.message.asset_data) === JSON.stringify(nextProps.message.asset_data) &&
    JSON.stringify(prevProps.message.cta_data) === JSON.stringify(nextProps.message.cta_data) &&
    prevProps.message.need_cta === nextProps.message.need_cta &&
    prevProps.message.campaign_text === nextProps.message.campaign_text &&
    JSON.stringify(prevProps.message.banner_cta_data) === JSON.stringify(nextProps.message.banner_cta_data) &&
    prevProps.disabled === nextProps.disabled
  );
});